import styled from '@emotion/styled';
import { Input, message, Pagination, Select, Spin, Tooltip } from 'antd';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import CheckContainer from 'components/CheckContainer';
import Empty from 'components/Empty';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import useCrud from 'hooks/useCrud';
import useSmartField from 'hooks/useSmartField';
import { HTMLAttributes, useCallback, useEffect, useMemo, useState, useRef, memo } from 'react';
import { useTranslate } from '@tolgee/react';
import { resolve } from 'services/api';
import { getAdminStores, GetAdminStoresParams } from 'services/api/admin';
import { aggregationWabaList, AggregationWabaListParams } from 'services/api/whatsapp';
import { getDepartmentScopes } from './useConfigList';
import { FV, Store } from './share';
import useCheck from './useCheck';
import { CountryFlagIcon } from '../CountryFlagIcon';

export interface StoreListProps extends HTMLAttributes<HTMLDivElement> {
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion | 'all';
  departmentId?: number;
  permId?: General.PermissionId;
  storeDataSource?: Store[];
  invalidStoreIds?: {
    storeIds: number[];
    tooltips: string;
  }[];
}

const defaultPageSize = 20;

function StoreList(props: StoreListProps) {
  const {
    platform,
    region,
    departmentId,
    permId,
    storeDataSource,
    invalidStoreIds,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const { config: storeNameConfig } = useStoreNameConfig();

  const [keywords, setKeywords] = useState('');
  const [storeTypeValue, setStoreTypeValue] = useState<string[]>([]);

  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [total, setTotal] = useState(0);

  const storeTypeOptions = useMemo(() => {
    if (platform === 'SHOPEE') {
      return [
        {
          label: t('knowledge.LOCAL'),
          value: 'LOCAL',
        },
        {
          label: t('knowledge.CROSS'),
          value: 'CROSS',
        },
        {
          label: '3PF',
          value: '3PF',
        },
      ];
    }

    return [
      {
        label: t('knowledge.LOCAL'),
        value: 'LOCAL',
      },
      {
        label: t('knowledge.CROSS'),
        value: 'CROSS',
      },
    ];
  }, [platform, t]);

  const { loading, list, fetchList } = useCrud({
    readList: useCallback(
      (params: GetAdminStoresParams) => {
        if (storeDataSource) {
          const { page, pageSize = 20, externalType: platform, region, keyWord } = params;

          const filteredStoreList = storeDataSource.filter((store) => {
            if (
              (platform && platform !== store.externalType) ||
              (region && region !== store.region) ||
              (keyWord &&
                !store.storeName.includes(keyWord) &&
                !store.alias.includes(keyWord) &&
                !store.externalId.includes(keyWord))
            ) {
              return false;
            }

            return true;
          });

          const storeList = filteredStoreList.slice((page - 1) * pageSize, page * pageSize);

          setTotal(filteredStoreList.length);

          return Promise.resolve(storeList);
        }

        return resolve(getAdminStores(params)).then((res) => {
          const { total, stores } = res;

          setTotal(total);

          return stores;
        });
      },
      [storeDataSource],
    ),
  });

  const refsCurrent = {
    fetchList,
    t,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  const filterList = useMemo(() => {
    if (storeTypeValue.length > 0) {
      return list?.filter((store) => {
        if (storeTypeValue.includes('3PF') && store.is3pf) {
          return true;
        }

        return storeTypeValue.includes(store.storeType);
      });
    }
    return list;
  }, [list, storeTypeValue]);

  useEffect(() => {
    refs.current
      .fetchList({
        externalType: platform,
        region: region === 'all' ? undefined : region,
        keyWord: keywords,
        page: current,
        pageSize,
        departmentId,
        permId,
      })
      .catch(() => {
        message.error(refs.current.t('marketing.获取店铺列表失败，请稍后再试'));
      });
  }, [platform, region, keywords, current, pageSize, departmentId, permId]);

  const { onStoreChecked, isChecked } = useCheck();

  const checkeAll = useMemo(() => {
    if (!filterList || filterList.length === 0) {
      return <div />;
    }

    const checked = filterList.every(({ id, externalType, region }) => {
      return isChecked({
        idList: [id],
        platform: externalType,
        region,
      });
    });

    const indeterminate =
      !checked &&
      filterList.some(({ id, externalType, region }) => {
        return isChecked({
          idList: [id],
          platform: externalType,
          region,
        });
      });

    return (
      <Checkbox
        checked={checked}
        indeterminate={indeterminate}
        onChange={({ target: { checked } }) => onStoreChecked(checked, filterList)}
      >
        {t('department.本页全部')}
      </Checkbox>
    );
  }, [filterList, onStoreChecked, isChecked, t]);

  const header = (
    <header>
      {checkeAll}
      <div className="flex items-center gap-1">
        <Select
          className="!w-40"
          mode="multiple"
          allowClear
          placeholder={t('ViewShops.店铺类型')}
          maxTagCount={1}
          options={storeTypeOptions}
          value={storeTypeValue}
          onChange={setStoreTypeValue}
          showArrow
        />
        <Input.Search
          className="!w-48"
          placeholder={t('store_list_search_placeholder')}
          defaultValue={keywords}
          onSearch={(keywords) => {
            setKeywords(keywords);
            setCurrent(1);
          }}
        />
      </div>
    </header>
  );

  const content = useMemo(() => {
    if (!filterList || filterList.length === 0) {
      return <Empty />;
    }

    return (
      <Content>
        {filterList.map((store) => {
          const { id, storeName, alias, externalType, region } = store;

          const props = {
            idList: [id],
            platform: externalType,
            region,
          };

          const name = isStoreAliasFirst(storeNameConfig, externalType, region)
            ? alias || storeName
            : storeName;

          const invalidTooltips = invalidStoreIds?.find(({ storeIds }) =>
            storeIds.includes(id),
          )?.tooltips;

          const checked = isChecked(props);

          if (!invalidTooltips || checked) {
            return (
              <div key={id}>
                <CheckContainer
                  key={id}
                  checked={checked}
                  onChange={(checked) => onStoreChecked(checked, [store])}
                >
                  <CountryFlagIcon region={region} />
                  <span title={name}>{name}</span>
                </CheckContainer>
              </div>
            );
          }

          return (
            <Tooltip key={id} title={invalidTooltips}>
              <div>
                <CheckContainer
                  checked={checked}
                  style={{ background: '#D3D7E0', cursor: 'not-allowed' }}
                >
                  <CountryFlagIcon region={region} />
                  <span title={name}>{name}</span>
                </CheckContainer>
              </div>
            </Tooltip>
          );
        })}
      </Content>
    );
  }, [filterList, storeNameConfig, isChecked, onStoreChecked, invalidStoreIds]);

  const pagination = (
    <Pagination
      size="small"
      current={current}
      pageSize={pageSize}
      total={total}
      showTotal={(x) => t('feedback.共x项', { x })}
      showSizeChanger
      pageSizeOptions={['20', '50', '100']}
      onChange={(current, pageSize) => {
        setCurrent(current);
        setPageSize(pageSize);
      }}
    />
  );

  return (
    <Container {...otherProps}>
      {header}
      <Spin spinning={loading}>{content}</Spin>
      {filterList && filterList.length > 0 && <footer>{pagination}</footer>}
    </Container>
  );
}

export type WabaListProps = Omit<StoreListProps, 'region' | 'platform'>;

function WabaList(props: WabaListProps) {
  const { departmentId, ...otherProps } = props;

  const { t } = useTranslate();

  const [keywords, setKeywords] = useState('');

  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [total, setTotal] = useState(0);

  const idsRef = useRef<number[]>();

  const { loading, list, fetchList } = useCrud({
    readList: useCallback(
      (params: AggregationWabaListParams) => {
        const getIds = () => {
          if (idsRef.current) {
            return Promise.resolve(idsRef.current);
          }

          if (!departmentId) {
            return Promise.resolve([]);
          }

          return getDepartmentScopes(departmentId).then((res) => {
            const target = res.find(({ platform }) => platform === 'WA_BIZ');

            const { regionStoreScopes: [{ storeIds = [] }] = [] } = target || {};

            idsRef.current = storeIds;

            return storeIds;
          });
        };

        return getIds().then((ids) => {
          return aggregationWabaList({
            ...params,
            tunnelAccountIds: ids.length === 0 ? undefined : ids,
          }).then((res) => {
            const { totalAll, accounts } = res;

            setTotal(totalAll);

            return accounts;
          });
        });
      },
      [departmentId],
    ),
  });

  const refsCurrent = {
    fetchList,
    t,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const {
      current: { fetchList },
    } = refs;

    fetchList({
      keyword: keywords,
      pageNum: current,
      pageSize,
    }).catch(() => {
      message.error(refs.current.t('department.获取waba列表失败，请稍后再试'));
    });
  }, [keywords, current, pageSize]);

  const { onWabaChecked, isChecked, isIndeterminate } = useCheck();

  const checkeAll = useMemo(() => {
    if (!list || list.length === 0) {
      return <div />;
    }

    const idList = list.map(({ tunnelAccountId }) => Number(tunnelAccountId));

    const props = {
      idList,
      platform: 'WA_BIZ',
      region: 'all',
    } as const;

    const checked = isChecked(props);

    const indeterminate = !checked && isIndeterminate(props);

    return (
      <Checkbox
        checked={checked}
        indeterminate={indeterminate}
        onChange={({ target: { checked } }) => onWabaChecked(checked, list)}
      >
        {t('department.本页全部')}
      </Checkbox>
    );
  }, [list, onWabaChecked, isChecked, isIndeterminate, t]);

  const header = (
    <header>
      {checkeAll}
      <Input.Search
        className="!w-48"
        placeholder={t('department.ID、手机号')}
        defaultValue={keywords}
        onSearch={(keywords) => {
          setKeywords(keywords);
          setCurrent(1);
        }}
      />
    </header>
  );

  const content = useMemo(() => {
    if (!list || list.length === 0) {
      return <Empty />;
    }

    return (
      <Content>
        {list.map((waba) => {
          const { tunnelAccountId, phone } = waba;

          const idList = [Number(tunnelAccountId)];

          const props = {
            idList,
            platform: 'WA_BIZ',
            region: 'all',
          } as const;

          return (
            <div key={tunnelAccountId}>
              <CheckContainer
                key={tunnelAccountId}
                checked={isChecked(props)}
                onChange={(checked) => onWabaChecked(checked, [waba])}
              >
                <span>{phone}</span>
              </CheckContainer>
            </div>
          );
        })}
      </Content>
    );
  }, [list, isChecked, onWabaChecked]);

  const pagination = (
    <Pagination
      size="small"
      current={current}
      pageSize={pageSize}
      total={total}
      showTotal={(x) => t('feedback.共x项', { x })}
      showSizeChanger
      pageSizeOptions={['20', '50', '100']}
      onChange={(current, pageSize) => {
        setCurrent(current);
        setPageSize(pageSize);
      }}
    />
  );

  return (
    <Container {...otherProps}>
      {header}
      <Spin spinning={loading}>{content}</Spin>
      {list && list.length > 0 && <footer>{pagination}</footer>}
    </Container>
  );
}

export default memo((props: Omit<StoreListProps, 'region' | 'platform'>) => {
  const { departmentId, ...otherProps } = props;

  const {
    field: { value: active },
  } = useSmartField<FV['active']>('active');

  if (!active) {
    return <Empty />;
  }

  const { platform, region } = active;

  const key = `${platform}@${region}`;

  if (platform === 'WA_BIZ') {
    return <WabaList departmentId={departmentId} {...otherProps} />;
  }

  return (
    <StoreList
      key={key}
      platform={platform}
      region={region}
      departmentId={departmentId}
      {...otherProps}
    />
  );
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 100%;
  > header {
    flex: 0 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    height: 40px;
    padding: 0 2px 0 16px;
    background-color: #fff;
    border-bottom: solid 1px #f0f2f5;
    z-index: 1;
  }
  > footer {
    flex: 0 0 auto;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    position: sticky;
    bottom: 0;
    height: 40px;
    padding: 0 16px;
    box-shadow: 0 -2px 8px 0 rgba(167, 178, 204, 0.1), inset 0 1px 0 0 #f0f2f5;
    background-color: #fff;
  }
  > div {
    flex: 1 0 0;
  }
`;

const Content = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 16px 0 0 16px;
  > div {
    flex: 0 0 50%;
    padding: 0 16px 10px 0;
    overflow: hidden;
    > label {
      width: 100%;
      padding: 8px 6px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      > * {
        margin-left: 4px;
      }
    }
  }
`;
